import * as THREE from 'three';
import { WeaveButton } from '@/components/UI/WeaveButton/WeaveButton';
import { WeaveDropdown, WeaveDropdownOption } from '@/components/UI/WeaveDropdown';
import { WeaveIconButton } from '@/components/UI/WeaveIconButton/WeaveIconButton';
import { WeaveSlider } from '@/components/UI/WeaveSlider';
import { Icon } from '@/components/UI/Icon/Icon';
import {
	SelectedViewPlaneStats,
	ViewPlaneBlockingMode,
	ViewPlaneInspectorResult,
	ViewPlaneVisualizationMode,
} from '@/store/threeSceneStore';
import { WorkflowProps } from '@/types/workflow';
import { handleAddViewScoreModal } from '@/components/AddViewScoreModal/AddViewScoreModal';
import styles from './styles.module.scss';

type SelectedViewPlaneStatsPanelProps = {
	stats: SelectedViewPlaneStats;
	visualizationMode: ViewPlaneVisualizationMode;
	onVisualizationModeChange: (mode: ViewPlaneVisualizationMode) => void;
	blockingMode: ViewPlaneBlockingMode;
	onBlockingModeChange: (mode: ViewPlaneBlockingMode) => void;
	categoryFilter: string | null;
	onCategoryFilterChange: (category: string | null) => void;
	inspectorResult: ViewPlaneInspectorResult | null;
	sampleScale: number;
	onSampleScaleChange: (scale: number) => void;
	onClose: () => void;
	scene?: THREE.Scene;
	workflowData?: WorkflowProps['workflowData'];
	onDataChange?: WorkflowProps['onDataChange'];
	activeSection?: WorkflowProps['activeSection'];
};

export const SelectedViewPlaneStatsPanel = ({
	stats,
	visualizationMode,
	onVisualizationModeChange,
	blockingMode,
	onBlockingModeChange,
	categoryFilter,
	onCategoryFilterChange,
	inspectorResult,
	sampleScale,
	onSampleScaleChange,
	onClose,
	scene,
	workflowData,
	onDataChange,
	activeSection,
}: SelectedViewPlaneStatsPanelProps) => {
	const handleAddViewScore = () => {
		if (!scene || !workflowData || !onDataChange) return;

		// Find the sampling plane to retrieve the stored window origin
		let planeOrigin: THREE.Vector3 | null = null;
		scene.traverse((obj: THREE.Object3D) => {
			if (!planeOrigin && obj.name === `__viewRaySamplingPlane__${stats.windowId}`) {
				const origin = obj.userData.origin;
				if (origin instanceof THREE.Vector3) {
					planeOrigin = origin.clone();
				}
			}
		});

		// Find all window objects and pick the closest one to the plane origin
		let windowGroup: THREE.Object3D | null = null;
		if (planeOrigin) {
			let minDistance = Infinity;
			scene.traverse((child: THREE.Object3D) => {
				if (child.userData.isWindow) {
					const winPos = new THREE.Vector3();
					child.getWorldPosition(winPos);
					const dist = winPos.distanceTo(planeOrigin!);
					if (dist < minDistance) {
						minDistance = dist;
						windowGroup = child;
					}
				}
			});
		}

		// Get window world position
		const windowPosition = new THREE.Vector3();
		if (windowGroup) {
			(windowGroup as THREE.Object3D).getWorldPosition(windowPosition);
		} else if (planeOrigin) {
			// Fall back to the plane origin if no window found
			windowPosition.copy(planeOrigin);
		} else {
			return;
		}

		// Find closest roomFillMesh — same logic as cameraControls.ts
		let closestRoomFillMesh: THREE.Mesh | null = null;
		let closestDistance = Infinity;
		scene.traverse((child: THREE.Object3D) => {
			if (child.userData.isRoomFloorFill && child instanceof THREE.Mesh) {
				const distance = child.position.distanceTo(windowPosition);
				if (distance < closestDistance) {
					closestDistance = distance;
					closestRoomFillMesh = child;
				}
			}
		});

		const workflowProps: WorkflowProps = {
			onDataChange,
			workflowData,
			activeSection: activeSection ?? 0,
			sectionLabel: 'View Analysis',
		};

		handleAddViewScoreModal(closestRoomFillMesh, windowGroup, windowPosition, workflowProps);
	};

	return (
		<div className={styles.viewPlaneStatsPanel}>
			<div className={styles.viewPlaneStatsHeaderRow}>
				<span className={styles.viewPlaneStatsHeader}>Plane Slice</span>
				<WeaveIconButton variant="outlined" onClick={onClose} className={styles.viewPlaneCloseButton}>
					<Icon name="weave-close" className={styles.viewPlaneCloseIcon} />
				</WeaveIconButton>
			</div>
			<div className={styles.helpText}>
				<span className={styles.helpLabel}>Tip:</span> Right-click on the window to add a view score
			</div>
			{workflowData && onDataChange && activeSection && (
				<WeaveButton
					variant="outlined"
					className={styles.addViewScoreButton}
					onClick={handleAddViewScore}
				>
					Add View Score
				</WeaveButton>
			)}
			<div className={styles.viewPlaneStatsMeta}>
				<span>{stats.totalIntersections} rays</span>
				<span>{stats.planeDistance.toFixed(1)}m out</span>
				<span>
					{stats.width.toFixed(1)}m x {stats.height.toFixed(1)}m
				</span>
				<span>avg {stats.averageDistance.toFixed(1)}m</span>
			</div>
			<div className={styles.viewPlaneModeRow}>
				<WeaveButton
					variant={visualizationMode === 'dots' ? 'solid' : 'outlined'}
					className={styles.viewPlaneModeButton}
					onClick={() => onVisualizationModeChange('dots')}
				>
					Dots
				</WeaveButton>
				<WeaveButton
					variant={visualizationMode === 'heatmap' ? 'solid' : 'outlined'}
					className={styles.viewPlaneModeButton}
					onClick={() => onVisualizationModeChange('heatmap')}
				>
					Heatmap
				</WeaveButton>
				<WeaveButton
					variant={visualizationMode === 'both' ? 'solid' : 'outlined'}
					className={styles.viewPlaneModeButton}
					onClick={() => onVisualizationModeChange('both')}
				>
					Both
				</WeaveButton>
			</div>
			<div className={styles.viewPlaneCategoryRow}>
				<span className={styles.viewPlaneCategoryLabel}>Category</span>
				<WeaveDropdown
					className={styles.viewPlaneCategorySelect}
					value={categoryFilter ?? 'all'}
					onChange={(detail) => {
						onCategoryFilterChange(detail.value === 'all' ? null : detail.value);
					}}
				>
					<WeaveDropdownOption value="all">All categories</WeaveDropdownOption>
					{stats.categories.slice(0, 6).map((entry) => (
						<WeaveDropdownOption key={`filter-${entry.category}`} value={entry.category}>
							{entry.category}
						</WeaveDropdownOption>
					))}
				</WeaveDropdown>
			</div>
			<div className={styles.viewPlaneBlockingRow}>
				<WeaveButton
					variant={blockingMode === 'plane' ? 'solid' : 'outlined'}
					className={styles.viewPlaneModeButton}
					onClick={() => onBlockingModeChange('plane')}
				>
					Plane Block
				</WeaveButton>
				<WeaveButton
					variant={blockingMode === 'source' ? 'solid' : 'outlined'}
					className={styles.viewPlaneModeButton}
					onClick={() => onBlockingModeChange('source')}
				>
					Source Block
				</WeaveButton>
			</div>
			<div className={styles.viewPlaneSampleRow}>
				<div className={styles.viewPlaneSampleHeaderRow}>
					<span className={styles.viewPlaneCategoryLabel}>Sample Size</span>
					<span className={styles.viewPlaneSampleValue}>{Math.round(sampleScale * 100)}%</span>
				</div>
				<WeaveSlider
					min={50}
					max={200}
					step={5}
					value={Math.round(sampleScale * 100)}
					onChange={(event: unknown) => {
						const target = event as { nativeEvent?: { detail?: string } };
						const rawValue = target.nativeEvent?.detail;
						const parsed = Number(rawValue);
						if (!Number.isNaN(parsed)) {
							onSampleScaleChange(parsed / 100);
						}
					}}
					className={styles.viewPlaneSampleSlider}
				/>
			</div>
			<div className={styles.viewPlaneStatsList}>
				{stats.categories.slice(0, 6).map((entry) => (
					<div key={entry.category} className={styles.viewPlaneStatsRow}>
						<div className={styles.viewPlaneStatsLabelWrap}>
							<span
								className={styles.viewPlaneStatsSwatch}
								style={{ backgroundColor: entry.color }}
							/>
							<span className={styles.viewPlaneStatsLabel}>{entry.category}</span>
						</div>
						<div className={styles.viewPlaneStatsValues}>
							<span>{entry.count}</span>
							<span>{entry.percentage.toFixed(0)}%</span>
							<span>{entry.averageDistance.toFixed(1)}m</span>
						</div>
					</div>
				))}
			</div>
			{(visualizationMode === 'heatmap' || visualizationMode === 'both') && (
				<div className={styles.heatmapLegend}>
					<span className={styles.legendItem}>
						<span className={styles.legendSwatch} style={{ background: 'rgb(34,197,94)' }} />
						open
					</span>
					<span className={styles.legendItem}>
						<span className={styles.legendSwatch} style={{ background: '#ef4444' }} />
						near block
					</span>
					<span className={styles.legendItem}>
						<span className={styles.legendSwatch} style={{ background: '#eab308' }} />
						far block
					</span>
				</div>
			)}
			{inspectorResult && (
				<div className={styles.viewPlaneInspector}>
					<div className={styles.viewPlaneInspectorHeader}>
						<span>Clicked sample</span>
						<span className={styles.viewPlaneInspectorMeta}>
							{inspectorResult.openCount} open · {inspectorResult.blockedCount} blocked
						</span>
					</div>
					<div className={styles.viewPlaneInspectorList}>
						{inspectorResult.categories.slice(0, 5).map((cat) => (
							<div key={cat.category} className={styles.viewPlaneInspectorRow}>
								<div className={styles.viewPlaneStatsLabelWrap}>
									<span
										className={styles.viewPlaneStatsSwatch}
										style={{ backgroundColor: cat.color }}
									/>
									<span className={styles.viewPlaneStatsLabel}>{cat.category}</span>
								</div>
								<div className={styles.viewPlaneInspectorRowMeta}>
									<span>{cat.count} rays</span>
									<span>{cat.avgHitDistance.toFixed(1)}m</span>
								</div>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
};
